import { useParams } from 'react-router-dom';
import { SizingLanding } from '@/components/SizingLanding';
import { parseRoomSizeSlug, getRoomTypeBySlug, getCadrForSqft } from '@/data/rooms';
import { WILDFIRE_TARGET, requiredCfm } from '@/lib/calculator';
import NotFound from './NotFound';

/** Handles /200-sq-ft and /bedroom style URLs. */
export default function RoomSizePage() {
  const { size = '' } = useParams<{ size: string }>();
  const sqft = parseRoomSizeSlug(size);
  const type = getRoomTypeBySlug(size);

  if (type) {
    const cadr = getCadrForSqft(type.sqft, type.ceilingFt);
    return (
      <SizingLanding
        path={`/${type.slug}`}
        title={`Air purifier size for a ${type.name.toLowerCase()}: CADR worked out`}
        h1={`What size air purifier for a ${type.name.toLowerCase()}?`}
        description={`A typical ${type.sqft} ft² ${type.name.toLowerCase()} with a ${type.ceilingFt} ft ceiling needs about ${cadr} CFM of clean air at 5 eACH. See the maths and adjust for your room.`}
        areaSqFt={type.sqft}
        ceilingFt={type.ceilingFt}
        concerns={type.concerns}
        intro={<p>Example: a {type.typicalDimensions.length} × {type.typicalDimensions.width} ft {type.name.toLowerCase()}. Your room is probably different — use your own measurements below.</p>}
        notes={type.notes}
      />
    );
  }
  if (sqft) {
    const cadr = getCadrForSqft(sqft);
    const smoke = Math.ceil(requiredCfm({ areaSqFt: sqft, ceilingFt: 8 }, WILDFIRE_TARGET.each) - 1e-9);
    return (
      <SizingLanding
        path={`/${sqft}-sq-ft`}
        title={`Air purifier for ${sqft} sq ft: ${cadr} CFM CADR at 5 eACH`}
        h1={`What CADR does a ${sqft} sq ft room need?`}
        description={`${sqft} ft² with an 8 ft ceiling: about ${cadr} CFM for general particles (5 eACH) or ${smoke} CFM smoke CADR during wildfire smoke. Maths shown.`}
        areaSqFt={sqft}
        ceilingFt={8}
        concerns={['general']}
        notes={[`For wildfire smoke the target rises to about ${smoke} CFM of smoke CADR (AHAM's floor-area rule).`, 'Taller ceilings need proportionally more: a 10 ft ceiling needs 25% more than 8 ft.', 'Use output at a speed you will actually run; maximum speed is often loud.']}
      />
    );
  }
  return <NotFound />;
}
